import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import api, { money, FALLBACK_IMG } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Seo from "@/components/Seo";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ShoppingCart, ArrowRight } from "lucide-react";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

function SidebarProduct({ p, onAdd }) {
  return (
    <div className="flex gap-3 items-center py-3 border-b border-border last:border-0" data-testid={`sidebar-product-${p.slug}`}>
      <Link to={`/product/${p.slug}`} className="shrink-0">
        <img src={p.image || FALLBACK_IMG} alt={p.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
          className="w-14 h-14 object-cover rounded-sm border border-border" />
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/product/${p.slug}`} className="text-sm leading-tight line-clamp-2 hover:text-primary transition-colors">{p.name}</Link>
        <p className="font-mono-label text-[11px] text-primary mt-1">{money(p.price)}</p>
      </div>
      <button onClick={() => onAdd(p)} data-testid={`sidebar-add-${p.slug}`} title="Add to cart"
        className="shrink-0 w-9 h-9 grid place-items-center border border-border rounded-full text-muted-foreground hover:text-primary hover:border-primary transition-colors">
        <ShoppingCart className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const { add, setOpen } = useCart();
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    window.scrollTo(0, 0);
    api.get(`/posts/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    api.get("/products").then((r) => {
      const lc = r.data.filter((p) => p.category === "Liquid Cultures" && p.in_stock !== false && p.stock_count !== 0);
      setProducts((lc.length ? lc : r.data).slice(0, 4));
    }).catch(() => {});
  }, [slug]);

  const addToCart = (p) => { add(p); setOpen?.(true); };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-24 max-w-4xl mx-auto px-5 sm:px-8 space-y-6">
        <Skeleton className="h-10 w-3/4" /><Skeleton className="h-72 w-full" /><Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen pt-40 pb-24 text-center px-5">
        <h1 className="font-display text-4xl font-light mb-4">Article not found</h1>
        <Link to="/blog" className="font-mono-label text-xs text-primary hover:underline">← Back to the blog</Link>
      </div>
    );
  }

  const { post, recent, related } = data;
  const canonical = `https://nc-mycology.com/blog/${post.slug}`;
  const cleanHtml = DOMPurify.sanitize(post.content || "");
  const absImage = post.featured_image_url && post.featured_image_url.startsWith("/")
    ? `https://nc-mycology.com${post.featured_image_url}` : post.featured_image_url;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article",
    headline: post.meta_title || post.title,
    description: post.meta_description || post.excerpt,
    image: absImage ? [absImage] : undefined,
    datePublished: post.published_at, dateModified: post.updated_at || post.published_at,
    author: { "@type": "Organization", name: post.author || "NC Mycology" },
    publisher: { "@type": "Organization", name: "NC Mycology",
      logo: { "@type": "ImageObject", url: "https://nc-mycology.com/nc-logo.png" } },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  return (
    <div className="min-h-screen pt-28 pb-24 max-w-6xl mx-auto px-5 sm:px-8">
      <Seo
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt}
        image={post.featured_image_url}
        canonical={canonical}
        type="article"
        article={post}
      >
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://nc-mycology.com/" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://nc-mycology.com/blog" },
            { "@type": "ListItem", position: 3, name: post.title, item: canonical },
          ],
        })}</script>
      </Seo>

      <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-1.5 font-mono-label text-[11px] text-muted-foreground hover:text-primary transition-colors mb-8">
        <ArrowLeft className="w-3.5 h-3.5" /> All Articles
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-12">
        {/* main */}
        <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="article-body">
          <span className="font-mono-label text-[10px] px-2 py-1 bg-primary/10 text-primary border border-primary/30">{post.category}</span>
          <h1 className="font-display font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-5 mb-4">{post.title}</h1>
          <p className="font-mono-label text-[11px] text-muted-foreground mb-8">{fmtDate(post.published_at)} · By {post.author || "NC Mycology"}</p>

          {post.featured_image_url && (
            <img src={post.featured_image_url} alt={post.title} onError={(e) => { e.currentTarget.style.display = "none"; }}
              className="w-full aspect-[16/9] object-cover rounded-sm border border-border mb-10" />
          )}

          <div className="blog-content prose-mycology max-w-none" dangerouslySetInnerHTML={{ __html: cleanHtml }} />
        </motion.article>

        {/* sticky sidebar */}
        <aside className="lg:sticky lg:top-28 h-fit space-y-8" data-testid="article-sidebar">
          <div className="border border-border bg-card/40 rounded-sm p-6">
            <p className="font-mono-label text-[11px] text-primary mb-4">Featured Products</p>
            {products.map((p) => <SidebarProduct key={p.slug} p={p} onAdd={addToCart} />)}
            <Link to="/shop?category=Liquid+Cultures" className="mt-4 inline-flex items-center gap-1.5 font-mono-label text-[11px] text-primary hover:underline">
              Shop all cultures <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {recent?.length > 0 && (
            <div className="border border-border bg-card/40 rounded-sm p-6">
              <p className="font-mono-label text-[11px] text-primary mb-4">Recent Articles</p>
              <div className="space-y-3">
                {recent.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`recent-${r.slug}`}
                    className="block group">
                    <p className="text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2">{r.title}</p>
                    <p className="font-mono-label text-[10px] text-muted-foreground mt-1">{fmtDate(r.published_at)}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </aside>
      </div>

      {/* related guides */}
      {related?.length > 0 && (
        <section className="mt-20 pt-12 border-t border-border" data-testid="related-guides">
          <p className="font-mono-label text-xs text-primary mb-8">Related Guides</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {related.map((r, i) => (
              <motion.div key={r.slug} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                data-testid={`related-card-${r.slug}`}>
                <Link to={`/blog/${r.slug}`} className="group flex flex-col h-full border border-border bg-card/40 rounded-sm overflow-hidden hover:border-primary/60 transition-colors">
                  <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                    <img src={r.featured_image_url || FALLBACK_IMG} alt={r.title}
                      onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute top-3 left-3 font-mono-label text-[10px] px-2 py-1 bg-background/85 backdrop-blur text-primary border border-primary/30">{r.category}</span>
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <p className="font-mono-label text-[10px] text-muted-foreground mb-3">{fmtDate(r.published_at)}</p>
                    <h3 className="font-display text-lg font-light leading-snug mb-3 group-hover:text-primary transition-colors line-clamp-2">{r.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 flex-1">{r.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 font-mono-label text-[11px] text-primary">
                      Read Guide <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
